import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import type { TPractitioner } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";
import type { SupabaseClient } from "~/integrations/supabase";
import { supabase } from "~/integrations/supabase/ssr.browser";
import type { TProfile } from "~/integrations/supabase/types";
import type { AuthSessionContext } from "~/modules.server/auth/service.server";
import type { UserDetailsData } from "~/modules.server/profile/user-details-service";

import { API_URL } from "./constants";
import { logger } from "./logger";
import { useUserDetails } from "~/hooks/queries/use-user-details";

export const acceptTerms = async () => {
    const response = await fetch(`${API_URL}/accept-terms`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
    });

    if (!response.ok) {
        throw new Error("Failed to accept terms");
    }

    return await response.json();
};

// Fetch user profile data - can be used on both client and server
export const fetchUserProfile = async (
    supabaseClient: SupabaseClient,
    userId: string,
): Promise<null | (TProfile & { practitioner: TPractitioner | null })> => {
    const { data, error } = await supabaseClient
        .from("profiles")
        // AIDEV-NOTE: Pass the result-type generic so Supabase skips GetResult
        // inference — the combined schema pushes `select("*, join(*)")` past TS's
        // instantiation-depth limit (TS2589). The row is re-cast below.
        .select<string, TProfile & Record<string, unknown>>(
            "*, practitioner!practitioner_profile_id_fkey(*)",
        )
        .eq("id", userId)
        .single();

    if (error) {
        return null;
    }

    return data as unknown as TProfile & {
        practitioner: TPractitioner | null;
    };
};

// Prefetch user profile data for server-side rendering

export const useUserProfile = () => {
    const query = useUserDetails();

    return {
        data: query.data?.profile,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
    };
};

export const useAcceptTerms = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({
            userType,
            practitionerInviteToken,
            consultantInviteToken,
        }: {
            userType?: "practitioner" | "client" | "consultant";
            practitionerInviteToken?: string;
            consultantInviteToken?: string;
        } = {}): Promise<{
            success?: boolean;
            practitionerCreated?: boolean;
            // AIDEV-NOTE: Set for consultant invites — the shared session the
            // recipient should land on after terms.
            redirectTo?: string;
        }> => {
            const result = await apiClient.post("/accept-terms", {
                userType,
                practitionerInviteToken,
                consultantInviteToken,
            });

            return result.json();
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["user"] });

            // If practitioner was created, invalidate practitioner queries
            if (data?.practitionerCreated) {
                queryClient.invalidateQueries({
                    queryKey: ["practitioner", "clients"],
                });
            }
        },
    });
};

// AIDEV-NOTE: Updates the user's fullName via api.account.update-name
export const useUpdateName = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ fullName }: { fullName: string }) => {
            const result = await apiClient.post("/account/update-name", {
                fullName,
            });
            if (!result.ok) {
                throw new Error("Failed to update name");
            }
            return result.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });
};

// AIDEV-NOTE: Marks onboarding as completed via api.account.complete-onboarding
export const useCompleteOnboarding = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const result = await apiClient.post(
                "/account/complete-onboarding",
                {},
            );
            return result.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });
};

export const useAccountResearchRelease = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { userId } = useSupabaseSession();

    // Get user profile data, which includes hasResearchRelease
    const { data: userProfile, isLoading } = useUserProfile();
    const hasResearchRelease = userProfile?.hasResearchRelease ?? false;

    // Mutation to update research release preference
    const { mutate: updateResearchRelease, isPending } = useMutation({
        mutationFn: async (enabled: boolean) => {
            logger
                .withMetadata({ userId, enabled })
                .info("Updating account session donation preference via API");

            const response = await apiClient.put("/account/research-release", {
                hasResearchRelease: enabled,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error ||
                        "Failed to update session donation preference",
                );
            }
            // Return the updated value from the API response if needed, or just success
            return await response.json();
        },
        onMutate: async (enabled) => {
            // Cancel any outgoing refetches for the user profile
            await queryClient.cancelQueries({
                queryKey: ["user"],
            });

            // Snapshot the previous profile value
            const previousProfile = queryClient.getQueryData(["user"]);

            // Optimistically update the user profile data in the cache
            queryClient.setQueryData(
                ["user"],
                (old: UserDetailsData | undefined) => {
                    if (!old) return old;
                    return {
                        ...old,
                        profile: {
                            ...old.profile,
                            hasResearchRelease: enabled,
                        },
                    };
                },
            );

            return { previousProfile }; // Return context with previous profile
        },
        onError: (error, enabled, context) => {
            // Log the error
            logger
                .withError(error)
                .withMetadata({ userId, enabled })
                .error("Failed to update session donation preference");

            // Show error toast
            toast.error("Failed to update session donation preference");

            // Revert the optimistic update using the context
            if (context?.previousProfile) {
                queryClient.setQueryData(["user"], context.previousProfile);
            } else {
                // If no context, invalidate to refetch
                queryClient.invalidateQueries({ queryKey: ["user"] });
            }
        },
        onSuccess: (data, enabled) => {
            // Show success toast
            toast.success(
                `Session donation ${
                    enabled ? "enabled" : "disabled"
                } for your account`,
            );

            // Invalidate the user query to ensure consistency after mutation
            queryClient.invalidateQueries({
                queryKey: ["user"],
            });
        },
    });

    return {
        hasResearchRelease,
        updateResearchRelease,
        isLoading,
        isPending,
    };
};

export function useIsPractitioner() {
    const { data, isLoading: isLoadingProfile, refetch } = useUserDetails();

    // Derived state variables
    const isPractitioner = !!data?.isPractitioner;
    const hasBaaSigned = !!data?.practitionerBaaAcceptedAt;
    const needsBaaSigning =
        isPractitioner &&
        data?.practitionerBaaNudge.kind === "signature-reminder";

    return {
        isPractitioner,
        hasBaaSigned,
        needsBaaSigning,
        isLoading: isLoadingProfile,
        refetch,
    };
}

export const useUserFeatureFlags = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { userId } = useSupabaseSession();

    const { data: userDetails, isLoading } = useUserDetails();
    const featureFlags = userDetails?.featureFlags ?? {};

    const { mutate: updateFeatureFlags, isPending } = useMutation({
        mutationFn: async ({
            flagName,
            value,
        }: {
            flagName: string;
            value: boolean;
        }) => {
            logger
                .withMetadata({ userId, flagName, value })
                .info("Updating feature flag via API");

            const response = await apiClient.put("/account/feature-flags", {
                [flagName]: value,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error || "Failed to update feature flags",
                );
            }

            return await response.json();
        },
        onMutate: async ({ flagName, value }) => {
            await queryClient.cancelQueries({ queryKey: ["user"] });

            const previousProfile = queryClient.getQueryData(["user"]);

            queryClient.setQueryData(
                ["user"],
                (old: UserDetailsData | undefined) => {
                    if (!old) return old;
                    return {
                        ...old,
                        featureFlags: {
                            ...(old.featureFlags || {}),
                            [flagName]: value,
                        },
                    };
                },
            );

            return { previousProfile };
        },
        onError: (error, { flagName, value }, context) => {
            logger
                .withError(error)
                .withMetadata({ userId, flagName, value })
                .error("Failed to update feature flags");

            toast.error("Failed to update feature flags");

            if (context?.previousProfile) {
                queryClient.setQueryData(["user"], context.previousProfile);
            } else {
                queryClient.invalidateQueries({ queryKey: ["user"] });
            }
        },
        onSuccess: (data, { flagName, value }) => {
            toast.success(
                `Feature flag '${flagName}' ${
                    value ? "enabled" : "disabled"
                } for your account`,
            );
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });

    return {
        featureFlags,
        updateFeatureFlags: (flagName: string, value: boolean) =>
            updateFeatureFlags({ flagName, value }),
        isLoading,
        isPending,
    };
};
